// Star Trails: connect every star with one unbroken trail of straight lines, using as few lines as possible.
// The sky is a 100 x 100 square; a trail is a list of corner points and each pair of neighbours is one line.

export interface Pt {
  x: number;
  y: number;
}

export const SKY = 100;

export interface StarsSpec {
  kind: "lattice" | "constellation";
  stars: Pt[];
  /** at most this many straight lines are allowed */
  lines: number;
  /** a star counts as visited when a line passes within this distance of its centre */
  tol: number;
  /** grid the corners softly snap to (lattice puzzles), and where that grid starts */
  snapStep: number | null;
  snapOrigin: Pt;
  /** corners within this distance of a star centre snap onto it */
  starSnap: number;
  /** one valid trail, used by "show me" */
  solution: Pt[];
  title: string;
}

export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  let t = l2 === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** which stars are visited by the trail */
export function visited(stars: readonly Pt[], trail: readonly Pt[], tol: number): boolean[] {
  return stars.map((s) => {
    if (trail.length === 1) return Math.hypot(s.x - trail[0].x, s.y - trail[0].y) <= tol;
    for (let i = 1; i < trail.length; i++) if (distToSegment(s, trail[i - 1], trail[i]) <= tol) return true;
    return false;
  });
}

export const linesUsed = (trail: readonly Pt[]): number => Math.max(0, trail.length - 1);

export function isSolved(spec: StarsSpec, trail: readonly Pt[]): boolean {
  if (trail.length < 2 || linesUsed(trail) > spec.lines) return false;
  return visited(spec.stars, trail, spec.tol).every(Boolean);
}

/** corner placement: snap onto a star, or onto the puzzle's grid when one is set and the pointer is close to it */
export function snapPoint(spec: StarsSpec, p: Pt): Pt {
  let best: Pt | null = null;
  let bestD = spec.starSnap;
  for (const s of spec.stars) {
    const d = Math.hypot(s.x - p.x, s.y - p.y);
    if (d <= bestD) {
      best = s;
      bestD = d;
    }
  }
  if (best) return best;
  if (spec.snapStep) {
    const step = spec.snapStep;
    const gx = spec.snapOrigin.x + Math.round((p.x - spec.snapOrigin.x) / step) * step;
    const gy = spec.snapOrigin.y + Math.round((p.y - spec.snapOrigin.y) / step) * step;
    if (Math.hypot(gx - p.x, gy - p.y) <= spec.starSnap) return { x: gx, y: gy };
  }
  return p;
}

/**
 * Groups of three or more stars that lie on one straight line (within tolerance), largest first.
 * These are what the "Pebble notices…" hint points at.
 */
export function collinearGroups(stars: readonly Pt[], tol: number): number[][] {
  const groups = new Map<string, number[]>();
  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const a = stars[i];
      const b = stars[j];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len === 0) continue;
      const members: number[] = [];
      stars.forEach((s, k) => {
        // distance to the infinite line through a and b
        const d = Math.abs((b.x - a.x) * (a.y - s.y) - (a.x - s.x) * (b.y - a.y)) / len;
        if (d <= tol) members.push(k);
      });
      if (members.length >= 3) groups.set(members.join(","), members);
    }
  }
  // drop groups that are contained in a bigger one
  const all = [...groups.values()].sort((p, q) => q.length - p.length);
  const out: number[][] = [];
  for (const g of all) if (!out.some((o) => g.every((m) => o.includes(m)))) out.push(g);
  return out;
}
